import { Head, Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Equipment {
  id: number
  display_name: string
  requires_odometer: boolean
}

interface User {
  id: number
  name: string
}

interface AssignmentData {
  id: number
  user_id: number | null
  assigned_at: string | null
  start_odometer: number | null
  end_odometer: number | null
  returned_at: string | null
  notes: string
}

interface Props {
  equipment: Equipment
  assignment: AssignmentData
  users: User[]
  errors?: Record<string, string[]>
}

export default function EditAssignment({
  equipment,
  assignment,
  users,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Equipment", href: "/equipment" },
    { title: equipment.display_name, href: `/equipment/${equipment.id}` },
    {
      title: "Assignment History",
      href: `/equipment/${equipment.id}/assignment_history`,
    },
    {
      title: "Edit Assignment",
      href: `/equipment/${equipment.id}/edit_assignment/${assignment.id}`,
    },
  ]

  const form = useForm({
    user_id: assignment.user_id ?? "",
    assigned_at: assignment.assigned_at ?? "",
    start_odometer: assignment.start_odometer ?? "",
    end_odometer: assignment.end_odometer ?? "",
    returned_at: assignment.returned_at ?? "",
    notes: assignment.notes ?? "",
  })

  form.transform((data) => ({ equipment_assignment: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.patch(`/equipment/${equipment.id}/edit_assignment/${assignment.id}`)
  }

  const fieldErrors = errors ?? {}

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit Assignment - ${equipment.display_name}`} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Edit Assignment - {equipment.display_name}
          </h1>
          <Link
            href={`/equipment/${equipment.id}/assignment_history`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            &larr; Assignment History
          </Link>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="user_id"
                className="mb-1 block text-sm font-medium"
              >
                Assign To <span className="text-hpi-red">*</span>
              </label>
              <select
                id="user_id"
                value={form.data.user_id}
                onChange={(e) => form.setData("user_id", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="">Select Employee...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
              {fieldErrors.user && (
                <p className="text-hpi-red mt-1 text-sm">
                  {fieldErrors.user.join(", ")}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="assigned_at"
                className="mb-1 block text-sm font-medium"
              >
                Date Assigned <span className="text-hpi-red">*</span>
              </label>
              <input
                type="datetime-local"
                id="assigned_at"
                value={form.data.assigned_at}
                onChange={(e) => form.setData("assigned_at", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
              {fieldErrors.assigned_at && (
                <p className="text-hpi-red mt-1 text-sm">
                  {fieldErrors.assigned_at.join(", ")}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="start_odometer"
                  className="mb-1 block text-sm font-medium"
                >
                  Start Odo/Hours{" "}
                  {equipment.requires_odometer && (
                    <span className="text-hpi-red">*</span>
                  )}
                </label>
                <input
                  type="number"
                  id="start_odometer"
                  step="any"
                  value={form.data.start_odometer}
                  onChange={(e) =>
                    form.setData("start_odometer", e.target.value)
                  }
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                />
                {fieldErrors.start_odometer && (
                  <p className="text-hpi-red mt-1 text-sm">
                    {fieldErrors.start_odometer.join(", ")}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="end_odometer"
                  className="mb-1 block text-sm font-medium"
                >
                  End Odo/Hours
                </label>
                <input
                  type="number"
                  id="end_odometer"
                  step="any"
                  value={form.data.end_odometer}
                  onChange={(e) => form.setData("end_odometer", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                />
                {fieldErrors.end_odometer && (
                  <p className="text-hpi-red mt-1 text-sm">
                    {fieldErrors.end_odometer.join(", ")}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="returned_at"
                className="mb-1 block text-sm font-medium"
              >
                Date Returned
              </label>
              <input
                type="datetime-local"
                id="returned_at"
                value={form.data.returned_at}
                onChange={(e) => form.setData("returned_at", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
              {fieldErrors.returned_at && (
                <p className="text-hpi-red mt-1 text-sm">
                  {fieldErrors.returned_at.join(", ")}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="notes" className="mb-1 block text-sm font-medium">
                Notes <span className="text-hpi-red">*</span>
              </label>
              <textarea
                id="notes"
                rows={4}
                value={form.data.notes}
                onChange={(e) => form.setData("notes", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
              {fieldErrors.notes && (
                <p className="text-hpi-red mt-1 text-sm">
                  {fieldErrors.notes.join(", ")}
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={form.processing}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-6 py-2 text-white disabled:opacity-50"
              >
                {form.processing ? "Saving..." : "Save Changes"}
              </button>
              <Link
                href={`/equipment/${equipment.id}/assignment_history`}
                className="border-hpi-light-gray hover:bg-hpi-lightest-gray rounded border px-6 py-2"
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  )
}
